import { Controller } from "@hotwired/stimulus"

// The run event tree, transliterated from the reference implementation's event-stream tree
// renderer (GLY / val / detailHTML / statusTag / nodeHTML). Styling lives in the dashboard
// design layer's .etree rules; this controller renders one run's tree from its JSON value
// and wires the reference's interactions (collapse on operation rows, show-all on assets).
const GLYPH_PREFIXES = [
  ["run", "run"], ["discovery", "phase"], ["analysis", "phase"], ["scoring", "phase"],
  ["persist", "phase"], ["domain", "domain"], ["securitytxt", "txt"], ["securitydns", "dns"],
  ["securitypage", "page"], ["securityprogram", "program"], ["contact", "contact"],
  ["organization", "org"], ["listing", "directory"],
]

const SIGNAL_ORDER = [
  "has_scope", "safe_harbor_promissory", "testing_authorized", "cfaa_carveout", "dmca_carveout",
  "tos_carveout", "cvd_timeline", "offers_bounty", "offers_swag", "has_hall_of_fame", "read_level",
]

const STATE_LABEL = { verified: "verified", guessed: "inferred", not_set: "not set" }

export default class extends Controller {
  static values = { tree: Array }

  connect() {
    this.element.innerHTML = (this.treeValue || []).map(node => this.nodeHTML(node)).join("")
    for (const row of this.element.querySelectorAll(".row.op")) {
      row.addEventListener("click", () => row.parentElement.classList.toggle("closed"))
    }
    for (const button of this.element.querySelectorAll(".showall")) {
      button.addEventListener("click", (event) => {
        event.stopPropagation()
        button.closest(".asec").classList.add("expanded")
        button.remove()
      })
    }
  }

  glyph(type) {
    for (const [prefix, glyph] of GLYPH_PREFIXES) if (String(type).startsWith(prefix)) return glyph
    return "•"
  }

  val(node) {
    const d = node.data || {}
    if (node.type === "contact.verified") {
      const parts = [`${d.checked || 0} checked`]
      if (d.url_reachable) parts.push("url reachable")
      if (d.has_mx) parts.push("MX")
      return this.esc(parts.join(" · "))
    }
    if (node.type === "organization.scored") {
      const parts = [d.level, d.score, d.label].filter(x => x != null && x !== "")
      if (d.driver) parts.push(d.driver)
      return this.esc(parts.join(" · "))
    }
    const value = d.url || d.value || d.listing_url || node.subject || ""
    return this.esc(String(value).replace(/^https?:\/\//, ""))
  }

  detailHTML(node) {
    const d = node.data || {}
    if (!d.signals && !(d.assets && d.assets.length)) return ""
    const order = (a) => {
      const i = SIGNAL_ORDER.indexOf(a)
      return i < 0 ? 99 : i
    }
    let html = `<div class="detail">`
    if (d.signals) {
      const signals = (d.signals || []).slice().sort((a, b) =>
        order(a.attribute) - order(b.attribute) || String(a.attribute).localeCompare(String(b.attribute)))
      const present = signals.filter(s => s.state === "verified" || s.state === "guessed")
      html += `<div class="dhd">${present.length} of ${signals.length} signals present</div><div class="col">` +
        signals.map(s => `<div class="it"><span class="d ${s.state === "verified" ? "sv" : s.state === "guessed" ? "sg" : ""}"></span><span class="nm">${this.esc(s.attribute)}</span><span class="sb">${this.esc(STATE_LABEL[s.state] || s.state)}</span></div>`).join("") + `</div>`
    }
    if (d.assets && d.assets.length) {
      const assets = (d.assets || []).slice().sort((a, b) =>
        (a.scope === "in" ? 0 : 1) - (b.scope === "in" ? 0 : 1) || String(a.value).localeCompare(String(b.value)))
      const inCount = assets.filter(a => a.scope === "in").length
      const rows = assets.map((a, i) => `<div class="it ${a.scope === "out" ? "out" : ""} ${i >= 10 ? "more" : ""}"><span class="d ${a.scope === "in" ? "sv" : "so"}"></span><span class="nm">${this.esc(a.value)}</span><span class="sb">${this.esc(a.scope)} scope</span></div>`).join("")
      const button = assets.length > 10 ? `<button type="button" class="showall">show all ${assets.length}</button>` : ""
      html += `<div class="asec"><div class="dhd ahd${d.signals ? " sep" : ""}"><span>${assets.length} assets · ${inCount} in scope</span>${button}</div><div class="col assets">${rows}</div></div>`
    }
    return html + "</div>"
  }

  statusTag(node) {
    if (node.status) return `<span class="tag ${node.status === "ok" ? "ok" : "err"}">${this.esc(node.status)}</span>`
    const status = (node.data || {}).status
    if (status === "found" || status === "reachable") return `<span class="tag ok">${this.esc(status)}</span>`
    if (status === "missing" || status === "unreachable") return `<span class="tag off">${this.esc(status)}</span>`
    return ""
  }

  nodeHTML(node) {
    const kids = node.children || []
    const detail = this.detailHTML(node)
    const isOp = kids.length > 0 || detail !== ""
    const count = kids.length ? `<span class="count">${kids.length}</span>` : ""
    let html = `<div class="node"><div class="row ${isOp ? "op" : ""}"><span class="car ${isOp ? "" : "leaf"}">▾</span><span class="g">${this.esc(this.glyph(node.type))}</span><span class="ty">${this.esc(node.type)}</span><span class="dt">${this.val(node)}</span>${this.statusTag(node) || count}</div>`
    html += detail
    if (kids.length) html += `<div class="kids">${kids.map(k => this.nodeHTML(k)).join("")}</div>`
    return html + "</div>"
  }

  esc(value) {
    const div = document.createElement("div")
    div.textContent = String(value == null ? "" : value)
    return div.innerHTML
  }
}
